import React, { useState, useEffect, useRef } from "react";

import { Modal, Spin, Tooltip, Upload, message, Button, Popover } from "antd";
import { HiOutlineDocumentPlus } from "react-icons/hi2";
import { TbMessageQuestion } from "react-icons/tb";
import { FaXmark } from "react-icons/fa6";
import { FaRegThumbsDown, FaRegThumbsUp, FaThumbsUp, FaThumbsDown } from "react-icons/fa";

import "./styles.scss"
import { useDispatch, useSelector } from "react-redux";
import { promptOptimizeAction, promptOptimizeReset } from "../../../store/slices/ai-chat/prompt-genie";
import { GiMagicLamp } from "react-icons/gi";
import { ESGChatFeedbackAction } from "../../../store/slices/ai-chat/esgChat";

const { Dragger } = Upload;
const maxUploadfile = 5;
const maxFileSize = 4.5; //MB
const conversionOfMBtoBytes = 1048576;

const ChatInput = ({
    chatLoading,
    showDragger,
    showViewPlaybooks,
    questionInput,
    setQuestionInput,
    uploadedFiles,
    setUploadedFiles,
    handleSubmit,
    handleShowPromptsModal,
    handleShowEditModal,
    promptEvaluationFlag,
    handleShowUpdateModal,
    searchType,
    pendingTourItems
}) => {
    const [reachedMaxHeight, setReachedMaxHeight] = useState(false)
    const [showPlaybookTooltip, setShowPlaybookTooltip] = useState(false)
    const [showCustomPlaceholder, setShowCustomPlaceholder] = useState(true)
    const [showImprovedPrompt, setShowImprovedPrompt] = useState(false);
    const [showGenieLamp, setShowGenieLamp] = useState(true);
    const [feedback, setFeedback] = useState(null);


    const dispatch = useDispatch();
    const { data: optimizedPrompt, isLoading: optimizedPromptLoading } = useSelector((state) => state?.improvePrompt)
    const textareaRef = useRef(null);

    const handlePlaybookTooltip = () => {
        sessionStorage.setItem("playbook-tooltip", "hidden")
        setShowPlaybookTooltip(false)
    }

    const handleFocusQuestion = () => {
        setShowCustomPlaceholder(false)
    }

    const formatFile = async (currentFile) => {
        const convertToBase64 = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        };

        const base64File = await convertToBase64(currentFile);

        const structuredFile = {
            base64: base64File.split(";base64,")[1],
            format: currentFile?.type?.split("/")[1],
            fileName: currentFile.name?.split(".")[0]
        };

        return structuredFile
    };

    const handleClear = () => {
        setQuestionInput("")
        setShowGenieLamp(false)
    }

    const handleUploadFiles = async (file) => {
        const fileSizeLimit = maxFileSize * conversionOfMBtoBytes;

        if (file.size <= fileSizeLimit) {
            const structuredFile = await formatFile(file);

            setUploadedFiles(prevFiles => {
                const fileExists = prevFiles.some(oldFile => (oldFile.fileName === structuredFile.fileName) && (oldFile.format === structuredFile.format));

                if (!fileExists) {
                    const updatedFiles = [...prevFiles, structuredFile];
                    if (updatedFiles?.length <= maxUploadfile) {
                        return updatedFiles;
                    } else {
                        message.error("Sorry! Only 5 documents can be uploaded");
                        return prevFiles;
                    }
                } else {
                    message.error("File already exists");
                    return prevFiles;
                }
            });
        } else {
            message.error("Sorry! The file size exceeds 4.5MB!");
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit()
        }
    }

    const configureDragAndDrop = {
        name: 'file',
        multiple: true,
        beforeUpload: () => false,
        fileList: [],
        accept: '.pdf,.doc,.docx,.xls,.xlsx',
        onChange(info) {
            !chatLoading && handleUploadFiles(info.file);
        },
    };

    const handleQuestion = (e) => {
        setQuestionInput(e.target.value)

        if (e.target.value) {
            handlePlaybookTooltip()

            if (!showGenieLamp) {
                setShowGenieLamp(true);
            }
        }
    }

    const handleImprovePrompt = (e) => {
        const payload = {
            prompt: questionInput,
        }
        dispatch(promptOptimizeAction(payload))
        setShowGenieLamp(false)
        setFeedback(null);
    }

    const handleClosePrompt = () => {
        setShowImprovedPrompt(false);
        setShowGenieLamp(true);
        dispatch(promptOptimizeReset());
    }

    const handleAsk = () => {
        const updatedPrompt = optimizedPrompt?.optimizedPrompt;
        setQuestionInput(updatedPrompt);
        setShowImprovedPrompt(false);
        handleSubmit(updatedPrompt);
        dispatch(promptOptimizeReset())
    }

    const handleEdit = () => {
        setQuestionInput(optimizedPrompt?.optimizedPrompt)
        setShowImprovedPrompt(false);
        dispatch(promptOptimizeReset());
        setShowGenieLamp(true);
    }

    const handleFeedback = (value) => {
        if (feedback) return;
        setFeedback(value);
        const feedbackId = optimizedPrompt?.requestId;
        const payload = {
            userFeedback: value === "like" ? 'answer found' : 'answer not found'
        }
        dispatch(ESGChatFeedbackAction({ payload: payload, id: feedbackId }))
    }

    const content = (
        <div className="custom-popover-content" style={{ width: 500 }}>
            <div className="improved-prompt-section" style={{ maxHeight: 380, overflowY: "auto", marginBottom: 12 }}>
                <p className="improved-prompt">{optimizedPrompt?.optimizedPrompt}</p>
                <div className="feedback-container">
                    <Tooltip title="Helpful">
                        <span className={`like-icon ${feedback === "like" ? "submitted-like" : ""} ${feedback === "dislike" ? "disabled" : ""}`} onClick={() => handleFeedback("like")}>{feedback === "like" ? <FaThumbsUp /> : <FaRegThumbsUp />}</span></Tooltip>
                    <Tooltip title="Not Helpful">
                        <span className={`dislike-icon ${feedback === "dislike" ? "submitted-dislike" : ""}  ${feedback === "like" ? "disabled" : ""}`} onClick={() => handleFeedback("dislike")}>
                            {feedback === "dislike" ? <FaThumbsDown /> : <FaRegThumbsDown />}</span></Tooltip>
                </div>
            </div>
            <div className="footer-ask-btn-section" style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                <button className="footer-edit-btn" onClick={handleEdit}>
                    Edit
                </button>
                <button className="footer-ask-btn" onClick={handleAsk}>
                    Ask
                </button>
            </div>
            <div className="genie-icon">
                <GiMagicLamp />
            </div>
        </div>
    );

    useEffect(() => {
        const playbookTooltip = sessionStorage.getItem("playbook-tooltip")

        if (playbookTooltip !== "hidden") {
            setShowPlaybookTooltip(true)

            setTimeout(() => {
                handlePlaybookTooltip()
            }, 5000)
        }
    }, [])

    useEffect(() => {
        if (questionInput) {
            setShowCustomPlaceholder(false)
        }
    }, [questionInput])

    useEffect(() => {
        if (textareaRef.current.scrollHeight) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

            if (textareaRef.current.scrollHeight >= 125) {
                setReachedMaxHeight(true)
            } else {
                setReachedMaxHeight(false)
            }
        }
    }, [questionInput?.length]);

    useEffect(() => {
        if (optimizedPrompt?.optimizedPrompt) {
            setShowImprovedPrompt(true);
        }
    }, [optimizedPrompt])

    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === "Escape") {
                handleClosePrompt();
            }
        };

        window.addEventListener("keydown", handleEscKey);
        return () => {
            window.removeEventListener("keydown", handleEscKey);
        };
    }, []);

    useEffect(() => {
        dispatch(promptOptimizeReset())
        setShowImprovedPrompt(false);
    }, [])

    return (
        <div className="chat-input-container" >
            {uploadedFiles?.length > 0 && (
                <div className="uploaded-files">
                    {uploadedFiles?.map((file, index) => {
                        return (
                            <div className="uploaded-file-card" key={index}>
                                <div className="file-name">{`${file.fileName}.${file.format}`}</div>
                            </div>
                        )
                    })}
                </div>
            )}
            <div className={`chat-input-section ${reachedMaxHeight ? "adjust-chat-contents" : ""}`}>
                <div className={`chat-input-wrapper ${(promptEvaluationFlag && questionInput) ? `prompt-evaluation-screen` : ""}`}>
                    {optimizedPromptLoading && (
                        <div className="loading-overlay">
                            <Spin size="small" />
                            <span className="loading-text">Prompt Genie is working its magic...</span>
                        </div>
                    )}
                    {<textarea
                        ref={textareaRef}
                        className={`input-question input-textarea ${(promptEvaluationFlag && questionInput) ? `prompt-evaluation-screen` : ""} ${optimizedPromptLoading ? "loading-state" : ""}`}
                        name="chat-question"
                        type="text"
                        placeholder={promptEvaluationFlag ? `${"Choose a question from playbook"}` : `${"Ask a question"}`}
                        value={questionInput}
                        disabled={promptEvaluationFlag}
                        onChange={(e) => handleQuestion(e)}
                        onKeyDown={(e) => handleKeyPress(e)}
                        onFocus={() => handleFocusQuestion()}
                    />}

                    {((questionInput?.trim() && !promptEvaluationFlag) || pendingTourItems?.includes('company/research.promptGenie')) && (
                        <Tooltip title={<span>Prompt Genie</span>}>
                            <Popover
                                title={
                                    <div className="popover-header">
                                        <span>Prompt Genie's Recommendation</span>
                                        <FaXmark
                                            className="popover-close-icon"
                                            onClick={handleClosePrompt}
                                        />
                                    </div>
                                }
                                className="custom-popover"
                                content={content}
                                overlayClassName="enhanced-prompt-popover"
                                open={showImprovedPrompt}
                                trigger="click"
                                placement="topRight"
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                            >
                                <GiMagicLamp
                                    className={`research-bulb-icon-glow ${(showGenieLamp || pendingTourItems?.includes('company/research.promptGenie')) ? "" : "hidden-lamp"}`}
                                    data-tour-id="company/research.promptGenie"
                                    onClick={handleImprovePrompt}
                                />
                            </Popover>
                        </Tooltip>
                    )}

                    {questionInput && promptEvaluationFlag && (
                        <div className="clear-icon" onClick={handleClear}>
                            <FaXmark />
                        </div>
                    )}
                    {/* Custom Placeholder */}
                    {(showCustomPlaceholder) && (
                        <div className="placeholder">
                            <div className="line-1">{promptEvaluationFlag ? `${"Choose a question from playbook"}` : `${"Ask a question"}`}</div>
                            <div className="line-2">{`(Follow-up questions are not supported currently)`}</div>
                        </div>
                    )}

                    {/* Dragger*/}
                    {showDragger && (
                        <Dragger {...configureDragAndDrop}>
                            <div className="dragger-contents">
                                Upload
                                <HiOutlineDocumentPlus className="upload-icon" />
                            </div>
                        </Dragger>
                    )}
                </div>
                <div className="form-buttons-section">
                    {showPlaybookTooltip && (
                        <div className="view-playbook-tooltip-container" onClick={() => handleShowPromptsModal()}>
                            <div className="tooltip-contents">
                                Select question from Playbooks
                            </div>
                        </div>
                    )}
                    {(promptEvaluationFlag && questionInput) && (
                        <div className="form-button-default" onClick={() => handleShowUpdateModal()}>Update Playbook</div>
                    )}
                    {showViewPlaybooks && (
                        <div className="form-button-default" onClick={() => handleShowPromptsModal()}>View Playbooks</div>
                    )}
                    {(promptEvaluationFlag && questionInput) && (
                        <div className="form-button-default" onClick={() => handleShowEditModal()}>Edit Question</div>
                    )}
                    <div className={`form-button ${!questionInput ? "disabled" : ""}`} onClick={() => handleSubmit()} >Ask <TbMessageQuestion className="icon" /></div>
                </div>
            </div>
        </div>
    )
}

export default ChatInput